import React, { useState } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { HABI_API_BASE_URL, getAuthHeaders } from "../../utils/habiApi";
import { formatCurrencyUY, formatDateUY } from "../../utils/formatters";
import ScreenHeader from "../../components/ScreenHeader";
import {
  colors,
  typography,
  spacing,
  radius,
  cardBase,
  pageContainer,
  btnPrimary,
  btnSecondary,
  shadows,
} from "../../utils/designTokens";

/**
 * A2 — Conciliacion bancaria automatica.
 * Staff sube extracto (CSV o imagen) y obtiene matches sugeridos contra
 * payments pendientes del edificio. NO escribe back a Firestore — el
 * staff usa el flujo de Cobranza para aprobar el payment matcheado.
 */
export default function ConciliacionBancaria({ setSelected }) {
  const { selectedBuilding } = useBuilding();
  const { showToast } = useToast();

  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const handleProcess = async () => {
    if (!selectedBuilding) {
      showToast("Seleccioná un edificio.", "warning");
      return;
    }
    if (!file) {
      showToast("Subí un extracto bancario.", "warning");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast("El archivo supera 10MB.", "warning");
      return;
    }
    setProcessing(true);
    setResult(null);
    try {
      const headers = await getAuthHeaders();
      delete headers["Content-Type"];
      const fd = new FormData();
      fd.append("statement", file);
      fd.append("building_id", String(selectedBuilding.buildingid));

      const resp = await fetch(`${HABI_API_BASE_URL}/api/reconcile-bank-statement`, {
        method: "POST",
        headers,
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "No se pudo procesar el extracto.", "error");
        return;
      }
      const data = await resp.json();
      setResult(data);
      showToast(
        `Procesado: ${data.stats.total_movimientos} movimientos · ${data.stats.auto_approvable} verdes · ${data.stats.needs_review} amarillos · ${data.stats.orphans} huérfanos.`,
        "success"
      );
    } catch (e) {
      console.error("[A2] error:", e);
      showToast(e.message || "Error inesperado.", "error");
    } finally {
      setProcessing(false);
    }
  };

  if (!selectedBuilding) {
    return (
      <div style={pageContainer}>
        <ScreenHeader title="Conciliación bancaria" subtitle="Match automático extracto vs payments" />
        <div style={{ ...cardBase, padding: 24, marginTop: 16 }}>
          <p style={{ color: colors.gray600 }}>Seleccioná un edificio para conciliar.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageContainer}>
      <ScreenHeader
        title="Conciliación bancaria"
        subtitle={`${selectedBuilding.name || `Edificio ${selectedBuilding.buildingid}`} · subí extracto y revisá matches`}
      />

      {/* Upload card */}
      <div style={{ ...cardBase, padding: 20, marginTop: 16 }}>
        <h3 style={{ margin: 0, fontSize: typography.sizes.md, fontWeight: 400, color: colors.gray900 }}>
          Subir extracto
        </h3>
        <p style={{ fontSize: 13, color: colors.gray600, margin: "6px 0 16px" }}>
          Aceptado: CSV/TXT del banco o screenshot del extracto (PNG/JPG). Máx 10MB.
        </p>
        <input
          type="file"
          accept=".csv,.txt,image/*"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          style={{ width: "100%", marginBottom: 12 }}
        />
        {file && (
          <div style={{ fontSize: 12, color: colors.gray600, marginBottom: 12 }}>
            {file.name} · {(file.size / 1024).toFixed(0)} KB
          </div>
        )}
        <button
          type="button"
          onClick={handleProcess}
          disabled={processing || !file}
          style={{
            ...btnPrimary,
            opacity: processing || !file ? 0.6 : 1,
            cursor: processing || !file ? "not-allowed" : "pointer",
          }}
        >
          {processing ? "Procesando…" : "Extraer movimientos y matchear"}
        </button>
      </div>

      {/* Stats */}
      {result && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginTop: 16 }}>
          <StatCard label="Movimientos" value={result.stats.total_movimientos} bg={colors.gray50} />
          <StatCard label="Auto-aprobables" value={result.stats.auto_approvable} bg={colors.successLight} color={colors.success} />
          <StatCard label="A revisar" value={result.stats.needs_review} bg={colors.warningLight} color={colors.warning} />
          <StatCard label="Huérfanos" value={result.stats.orphans} bg={colors.errorLight} color={colors.error} />
        </div>
      )}

      {result && result.matches.length > 0 && (
        <div style={{ ...cardBase, padding: 16, marginTop: 16 }}>
          <h3 style={{ margin: 0, fontSize: typography.sizes.md, fontWeight: 400 }}>
            Movimientos con match sugerido
          </h3>
          <p style={{ fontSize: 12, color: colors.gray600, marginTop: 4 }}>
            Aprobá manualmente los matches verdes desde la pantalla de Cobranza. Esta vista es informativa.
          </p>

          <div style={{ marginTop: 12 }}>
            {result.matches.map((m, i) => (
              <MatchRow key={i} match={m} />
            ))}
          </div>
        </div>
      )}

      {result && result.orphans.length > 0 && (
        <div style={{ ...cardBase, padding: 16, marginTop: 16, borderLeft: `4px solid ${colors.error}` }}>
          <h3 style={{ margin: 0, fontSize: typography.sizes.md, fontWeight: 400, color: colors.error }}>
            Huérfanos ({result.orphans.length})
          </h3>
          <p style={{ fontSize: 12, color: colors.gray600, marginTop: 4 }}>
            Movimientos que no matchean con ningún payment pendiente. Pueden ser:
          </p>
          <ul style={{ margin: "4px 0 12px", paddingLeft: 18, fontSize: 12, color: colors.gray700 }}>
            <li>Pagos de un residente que cargó la transferencia con descriptor distinto.</li>
            <li>Pagos extra (multas, plan de pagos) no atados a un gasto común puntual.</li>
            <li>Errores del banco o transferencias cruzadas (revisar).</li>
          </ul>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {result.orphans.map((o, i) => (
              <div key={i} style={{ padding: 10, background: colors.gray50, borderRadius: 8, fontSize: 13 }}>
                <strong style={{ fontWeight: 400 }}>{formatCurrencyUY(o.monto)}</strong>
                {" · "}{o.fecha}
                {" · "}<span style={{ color: colors.gray600 }}>{o.concepto}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, bg, color }) {
  return (
    <div style={{
      padding: 14,
      background: bg,
      borderRadius: 10,
      textAlign: "center",
    }}>
      <div style={{ fontSize: 24, fontWeight: 400, color: color || colors.gray900 }}>{value}</div>
      <div style={{ fontSize: 11, color: color || colors.gray600, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </div>
    </div>
  );
}

function MatchRow({ match }) {
  const top = match.top_candidates?.[0];
  const score = top?.score || 0;
  const action = match.action;
  const bg =
    action === "approve" ? colors.successLight :
    action === "review" ? colors.warningLight :
    colors.errorLight;
  const accentColor =
    action === "approve" ? colors.success :
    action === "review" ? colors.warning :
    colors.error;

  return (
    <div style={{
      padding: 12,
      marginBottom: 8,
      background: bg,
      borderRadius: 8,
      display: "grid",
      gridTemplateColumns: "1fr auto",
      gap: 12,
      alignItems: "center",
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <strong style={{ fontWeight: 400, fontSize: typography.sizes.sm }}>
            {formatCurrencyUY(match.movimiento.monto)}
          </strong>
          <span style={{ fontSize: 12, color: colors.gray700 }}>{match.movimiento.fecha}</span>
          <span style={{
            padding: "2px 10px",
            fontSize: 11,
            borderRadius: 9999,
            background: accentColor,
            color: colors.white,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}>
            {action === "approve" ? "Auto" : action === "review" ? "Revisar" : "Manual"} · score {score}
          </span>
        </div>
        <div style={{ fontSize: 12, color: colors.gray700, marginBottom: 4 }}>
          Concepto: <em>{match.movimiento.concepto}</em>
        </div>
        {top && (
          <div style={{ fontSize: 12, color: colors.gray900 }}>
            → Match: <strong style={{ fontWeight: 400 }}>{top.payment_user || "(sin nombre)"}</strong>
            {top.payment_apt && <span> · Apto {top.payment_apt}</span>}
            {top.payment_period && <span> · {top.payment_period}</span>}
            {top.payment_amount && <span> · {formatCurrencyUY(top.payment_amount)}</span>}
          </div>
        )}
        {match.top_candidates && match.top_candidates.length > 1 && (
          <details style={{ marginTop: 4 }}>
            <summary style={{ fontSize: 11, color: colors.gray600, cursor: "pointer" }}>
              Otros {match.top_candidates.length - 1} candidatos
            </summary>
            <div style={{ marginTop: 4, fontSize: 11, color: colors.gray700 }}>
              {match.top_candidates.slice(1).map((c, i) => (
                <div key={i}>
                  {c.payment_user} · {c.payment_period} · score {c.score}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
